import Panzoom from '@panzoom/panzoom';
import { Popover } from 'bootstrap';

// Store point elements for legend interaction
const pointElements = new Map();

// Type-to-color mapping (defaults, overridden by settings)
let typeColors = {
    'Office': '#ffc107',
    'Industrial': '#0d6efd',
    'Retail': '#dc3545',
};

/**
 * Get pin color based on point type
 */
function getPointColor(point, fallbackColor) {
    if (point.type && typeColors[point.type]) {
        return typeColors[point.type];
    }
    return fallbackColor;
}

// Track state
let activePointId = null;
let expandedCluster = null;
let expandedClusterPoints = [];
let panzoomInstance = null;
let svgOverlayRef = null;
let legendItemsRef = null;
let suppressReset = false;

export default function initParkMap() {
    const mapContainer = document.querySelector('.park-map');
    if (!mapContainer) return;

    const config = window.parkMapConfig || {};
    const points = config.points || [];
    const settings = config.settings || {};

    // Load type colors from settings
    if (settings.typeColors) {
        typeColors = { ...typeColors, ...settings.typeColors };
    }

    const panzoomElement = mapContainer.querySelector('.park-map__panzoom');
    const svgOverlay = mapContainer.querySelector('.park-map__svg');
    const zoomInBtn = mapContainer.querySelector('.park-map__zoom-in');
    const zoomOutBtn = mapContainer.querySelector('.park-map__zoom-out');
    const resetBtn = mapContainer.querySelector('.park-map__zoom-reset');
    const legendItems = mapContainer.querySelectorAll('.park-map__legend-item[data-point-id]');

    if (!panzoomElement || !svgOverlay) return;

    // Initialize Panzoom
    const panzoom = Panzoom(panzoomElement, {
        maxScale: 5,
        minScale: 1,
        contain: 'outside',
        cursor: 'grab',
        panOnlyWhenZoomed: true,
    });

    // Store refs for use in popover close handlers
    panzoomInstance = panzoom;
    svgOverlayRef = svgOverlay;
    legendItemsRef = legendItems;

    // Track current scale for pin size adjustments
    let currentScale = 1;
    const baseRadius = settings.pointRadius || 12;

    // Update pin sizes when zoom changes
    const updatePinSizes = (scale) => {
        currentScale = scale;
        const adjustedRadius = baseRadius / scale;
        const adjustedStroke = 2 / scale;
        const clusterRadius = (baseRadius * 1.5) / scale;
        const fontSize = 12 / scale;

        svgOverlay.querySelectorAll('.park-map__point circle, .park-map__point--expanded circle').forEach(circle => {
            circle.setAttribute('r', adjustedRadius);
            circle.setAttribute('stroke-width', adjustedStroke);
        });

        svgOverlay.querySelectorAll('.park-map__cluster circle').forEach(circle => {
            circle.setAttribute('r', clusterRadius);
            circle.setAttribute('stroke-width', adjustedStroke);
        });

        svgOverlay.querySelectorAll('.park-map__cluster text').forEach(text => {
            text.setAttribute('font-size', fontSize);
        });
    };

    // Listen for zoom changes
    panzoomElement.addEventListener('panzoomchange', (e) => {
        updatePinSizes(e.detail.scale);
    });

    // Zoom controls
    if (zoomInBtn) {
        zoomInBtn.addEventListener('click', () => panzoom.zoomIn());
    }
    if (zoomOutBtn) {
        zoomOutBtn.addEventListener('click', () => panzoom.zoomOut());
    }
    if (resetBtn) {
        resetBtn.addEventListener('click', () => {
            panzoom.reset();
            clearActivePoint(legendItems);
            collapseExpandedCluster(svgOverlay);
        });
    }

    // Mouse wheel zoom
    panzoomElement.parentElement.addEventListener('wheel', panzoom.zoomWithWheel);

    // Cluster and render points
    const clusteredData = clusterPoints(points, settings.clusterThreshold || 5);
    renderPoints(svgOverlay, clusteredData, settings, panzoom, mapContainer);

    // Set legend marker colors based on type
    legendItems.forEach(item => {
        const pointId = parseInt(item.dataset.pointId);
        const point = points.find(p => p.id === pointId);
        if (point && point.type && typeColors[point.type]) {
            const marker = item.querySelector('.park-map__legend-marker');
            if (marker) {
                marker.style.backgroundColor = typeColors[point.type];
            }
        }
    });

    // Legend click interactions
    legendItems.forEach(item => {
        const pointId = parseInt(item.dataset.pointId);

        item.addEventListener('click', () => {
            const pointData = pointElements.get(pointId);
            if (!pointData) return;

            // Clear previous active state
            clearActivePoint(legendItems);

            // Set new active state
            activePointId = pointId;
            item.classList.add('park-map__legend-item--active');

            if (pointData.isCluster) {
                // Point is in a cluster - need to expand it
                handleClusteredPointClick(pointData, svgOverlay, settings, panzoom, mapContainer, pointId);
            } else {
                // Check if this point is part of the currently expanded cluster
                const isInExpandedCluster = expandedCluster &&
                    expandedClusterPoints.some(el => el.getAttribute('data-point-id') == pointId);

                if (isInExpandedCluster) {
                    // Point is in currently expanded cluster - just highlight and show popover
                    pointData.element.classList.add('park-map__point--highlighted');
                    showPointPopover(pointData.element);
                } else {
                    // Single point outside any expanded cluster - collapse and reset zoom
                    collapseExpandedCluster(svgOverlay);
                    panzoom.reset({ animate: true });
                    setTimeout(() => {
                        showPointPopover(pointData.element);
                        pointData.element.classList.add('park-map__point--highlighted');
                    }, 350);
                }
            }
        });
    });
}

/**
 * Clear active point state
 */
function clearActivePoint(legendItems) {
    // Remove active class from legend items
    legendItems.forEach(item => item.classList.remove('park-map__legend-item--active'));

    // Remove highlight from points
    document.querySelectorAll('.park-map__point--highlighted').forEach(el => {
        el.classList.remove('park-map__point--highlighted');
    });

    // Hide all popovers
    hideAllPopovers();

    activePointId = null;
}

/**
 * Collapse any expanded cluster
 */
function collapseExpandedCluster(svgOverlay) {
    if (expandedCluster) {
        expandedClusterPoints.forEach(el => el.remove());
        expandedClusterPoints = [];

        // Restore pointElements to reference the cluster (not the removed expanded elements)
        expandedCluster.cluster.points.forEach(point => {
            pointElements.set(point.id, {
                element: expandedCluster.element,
                point: point,
                isCluster: true,
                cluster: expandedCluster.cluster,
            });
        });

        expandedCluster.element.style.display = '';
        expandedCluster = null;
    }
}

/**
 * Handle click on a point that's in a cluster
 */
function handleClusteredPointClick(pointData, svgOverlay, settings, panzoom, mapContainer, targetPointId) {
    const cluster = pointData.cluster;
    const clusterElement = pointData.element;
    const scale = 3;

    // If clicking a point in a different cluster, reset and recalculate
    if (expandedCluster && expandedCluster.cluster !== cluster) {
        collapseExpandedCluster(svgOverlay);

        // Reset zoom first, then zoom to new cluster using percentage coordinates
        panzoom.reset({ animate: true });

        setTimeout(() => {
            zoomToClusterAndExpand(cluster, clusterElement, svgOverlay, settings, panzoom, mapContainer, targetPointId, scale);
        }, 300);
    } else {
        // Same cluster or no cluster expanded - zoom directly
        zoomToClusterAndExpand(cluster, clusterElement, svgOverlay, settings, panzoom, mapContainer, targetPointId, scale);
    }
}

/**
 * Zoom to cluster and expand it
 */
function zoomToClusterAndExpand(cluster, clusterElement, svgOverlay, settings, panzoom, mapContainer, targetPointId, scale) {
    const clusterCircle = clusterElement.querySelector('circle');
    const circleRect = clusterCircle.getBoundingClientRect();

    // Zoom to cluster location
    panzoom.zoomToPoint(scale, {
        clientX: circleRect.left + circleRect.width / 2,
        clientY: circleRect.top + circleRect.height / 2
    }, { animate: true });

    // Hide cluster indicator immediately (like direct click)
    clusterElement.style.display = 'none';
    expandedCluster = { element: clusterElement, cluster: cluster };

    // Create expanded points after brief delay (match direct click timing)
    setTimeout(() => {
        cluster.points.forEach((point) => {
            const color = getPointColor(point, settings.pointColor || '#0d6efd');
            const pointEl = createPointElement(
                point,
                color,
                settings.pointRadius || 12
            );
            pointEl.classList.add('park-map__point--expanded');
            svgOverlay.appendChild(pointEl);
            expandedClusterPoints.push(pointEl);
            initPopover(pointEl, point);

            // Store reference for this expanded point
            pointElements.set(point.id, { element: pointEl, point: point, isCluster: false });

            // If this is the target point, highlight and show popover
            if (point.id === targetPointId) {
                setTimeout(() => {
                    pointEl.classList.add('park-map__point--highlighted');
                    showPointPopover(pointEl);
                }, 200);
            }
        });

        // Update pin sizes for current zoom
        updateExpandedPinSizes(svgOverlay, settings.pointRadius || 12, scale);
    }, 100);
}

/**
 * Update pin sizes for expanded points
 */
function updateExpandedPinSizes(svgOverlay, baseRadius, scale) {
    const adjustedRadius = baseRadius / scale;
    const adjustedStroke = 2 / scale;

    svgOverlay.querySelectorAll('.park-map__point--expanded circle').forEach(circle => {
        circle.setAttribute('r', adjustedRadius);
        circle.setAttribute('stroke-width', adjustedStroke);
    });
}

/**
 * Zoom to a specific point
 */
function zoomToPoint(point, panzoom, mapContainer, callback) {
    const container = mapContainer.querySelector('.park-map__container');
    const panzoomElement = mapContainer.querySelector('.park-map__panzoom');
    const containerRect = container.getBoundingClientRect();
    const panzoomRect = panzoomElement.getBoundingClientRect();

    // Calculate point position on screen
    const currentScale = panzoom.getScale();
    const currentPan = panzoom.getPan();

    // Get the natural dimensions
    const naturalWidth = panzoomRect.width / currentScale;
    const naturalHeight = panzoomRect.height / currentScale;

    // Point position in natural coordinates
    const pointX = (point.x / 100) * naturalWidth;
    const pointY = (point.y / 100) * naturalHeight;

    // Determine target scale - zoom in if not already zoomed, otherwise keep current
    const targetScale = currentScale < 2 ? 2.5 : currentScale;

    // Calculate where the point currently is on screen
    const pointScreenX = containerRect.left + currentPan.x + (pointX * currentScale);
    const pointScreenY = containerRect.top + currentPan.y + (pointY * currentScale);

    // Use zoomToPoint to center on the point
    panzoom.zoomToPoint(targetScale, {
        clientX: pointScreenX,
        clientY: pointScreenY
    }, { animate: true });

    if (callback) {
        setTimeout(callback, 350);
    }
}

/**
 * Show popover for a point element
 */
function showPointPopover(element) {
    const popover = Popover.getInstance(element);
    if (popover) {
        popover.show();
    }
}

/**
 * Cluster nearby points based on distance threshold
 */
function clusterPoints(points, threshold) {
    if (!points.length) return { singles: [], clusters: [] };

    const used = new Set();
    const clusters = [];
    const singles = [];

    points.forEach((point, i) => {
        if (used.has(i)) return;

        const nearby = [];
        points.forEach((other, j) => {
            if (i === j || used.has(j)) return;

            const distance = Math.sqrt(
                Math.pow(point.x - other.x, 2) + Math.pow(point.y - other.y, 2)
            );

            if (distance <= threshold) {
                nearby.push({ index: j, point: other });
            }
        });

        if (nearby.length > 0) {
            // Create cluster with this point and nearby points
            const clusterPoints = [{ index: i, point }, ...nearby];
            clusterPoints.forEach(p => used.add(p.index));

            // Calculate cluster center
            const centerX = clusterPoints.reduce((sum, p) => sum + p.point.x, 0) / clusterPoints.length;
            const centerY = clusterPoints.reduce((sum, p) => sum + p.point.y, 0) / clusterPoints.length;

            clusters.push({
                x: centerX,
                y: centerY,
                points: clusterPoints.map(p => p.point),
                count: clusterPoints.length,
            });
        } else {
            singles.push(point);
            used.add(i);
        }
    });

    return { singles, clusters };
}

/**
 * Render points and clusters on the SVG overlay
 */
function renderPoints(svg, clusteredData, settings, panzoom, mapContainer) {
    const { singles, clusters } = clusteredData;
    const pointColor = settings.pointColor || '#0d6efd';
    const pointRadius = settings.pointRadius || 12;
    const clusterColor = settings.clusterColor || '#198754';

    // Clear existing points and stored references
    svg.innerHTML = '';
    pointElements.clear();

    // Render single points
    singles.forEach(point => {
        const color = getPointColor(point, pointColor);
        const group = createPointElement(point, color, pointRadius);
        svg.appendChild(group);

        // Add click handler to collapse clusters and reset zoom when clicking single points
        group.addEventListener('click', (e) => {
            hideAllPopovers();

            if (expandedCluster) {
                // Collapse cluster and reset zoom, then show popover after animation
                collapseExpandedCluster(svg);
                panzoom.reset({ animate: true });

                // Clear legend active state
                document.querySelectorAll('.park-map__legend-item--active').forEach(item => {
                    item.classList.remove('park-map__legend-item--active');
                });

                // Show popover after zoom animation completes
                setTimeout(() => {
                    Popover.getInstance(group)?.show();
                }, 350);
            } else {
                // No cluster expanded, show popover immediately
                Popover.getInstance(group)?.show();
            }
        });

        // Initialize popover with manualOnly flag (we handle showing it above)
        initPopover(group, point, { manualOnly: true });

        // Store reference for legend interaction
        pointElements.set(point.id, { element: group, point: point });
    });

    // Render clusters
    clusters.forEach(cluster => {
        const group = createClusterElement(cluster, clusterColor, pointRadius);
        svg.appendChild(group);
        initClusterBehavior(group, cluster, svg, settings, panzoom, mapContainer);

        // Store references for all points in cluster
        cluster.points.forEach(point => {
            pointElements.set(point.id, { element: group, point: point, isCluster: true, cluster: cluster });
        });
    });
}

/**
 * Create SVG group for a single point
 */
function createPointElement(point, color, radius) {
    const ns = 'http://www.w3.org/2000/svg';
    const group = document.createElementNS(ns, 'g');
    group.classList.add('park-map__point');
    group.setAttribute('data-point-id', point.id);
    group.style.cursor = 'pointer';

    const circle = document.createElementNS(ns, 'circle');
    circle.setAttribute('cx', `${point.x}%`);
    circle.setAttribute('cy', `${point.y}%`);
    circle.setAttribute('r', radius);
    circle.setAttribute('fill', color);
    circle.setAttribute('stroke', '#fff');
    circle.setAttribute('stroke-width', '2');

    group.appendChild(circle);
    return group;
}

/**
 * Create SVG group for a cluster
 */
function createClusterElement(cluster, color, baseRadius) {
    const ns = 'http://www.w3.org/2000/svg';
    const group = document.createElementNS(ns, 'g');
    group.classList.add('park-map__cluster');
    group.style.cursor = 'pointer';

    // Larger circle for cluster
    const radius = baseRadius * 1.5;

    const circle = document.createElementNS(ns, 'circle');
    circle.setAttribute('cx', `${cluster.x}%`);
    circle.setAttribute('cy', `${cluster.y}%`);
    circle.setAttribute('r', radius);
    circle.setAttribute('fill', color);
    circle.setAttribute('stroke', '#fff');
    circle.setAttribute('stroke-width', '2');

    // Count text
    const text = document.createElementNS(ns, 'text');
    text.setAttribute('x', `${cluster.x}%`);
    text.setAttribute('y', `${cluster.y}%`);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.setAttribute('fill', '#fff');
    text.setAttribute('font-size', '12');
    text.setAttribute('font-weight', 'bold');
    text.textContent = cluster.count;

    group.appendChild(circle);
    group.appendChild(text);
    return group;
}

/**
 * Initialize Bootstrap popover for a point
 */
function initPopover(element, point, options = {}) {
    const content = buildPopoverContent(point);

    new Popover(element, {
        html: true,
        trigger: 'manual',
        placement: 'top',
        title: point.asset_number ? `${point.label || 'Location'} <span class="park-map-popover__asset-number"> (Asset Reference Number ${point.asset_number})</span>` : (point.label || 'Location'),
        content: content,
        container: 'body',
        customClass: 'park-map-popover',
    });

    // Add click handler to show popover (and hide others)
    element.addEventListener('click', (e) => {
        // Don't show popover if this is handled elsewhere (e.g., single point collapsing cluster)
        if (options.manualOnly) return;

        hideAllPopovers();
        Popover.getInstance(element)?.show();
    });

    // Add close button and handle outside clicks when popover is shown
    element.addEventListener('shown.bs.popover', () => {
        const popoverInstance = Popover.getInstance(element);
        const popoverEl = popoverInstance?.tip;

        // Inject close button into header
        if (popoverEl) {
            const header = popoverEl.querySelector('.popover-header');
            if (header && !header.querySelector('.park-map-popover__close')) {
                const closeBtn = document.createElement('button');
                closeBtn.type = 'button';
                closeBtn.className = 'park-map-popover__close';
                closeBtn.setAttribute('aria-label', 'Close');
                closeBtn.innerHTML = '&times;';
                closeBtn.addEventListener('click', () => {
                    popoverInstance.hide();
                });
                header.appendChild(closeBtn);
            }
        }

        // Close popover when clicking elsewhere
        const closeOnClick = (e) => {
            if (!e.target.closest('.popover') && !e.target.closest('.park-map__point') && !e.target.closest('.park-map__point--expanded')) {
                Popover.getInstance(element)?.hide();
                document.removeEventListener('click', closeOnClick);
            }
        };
        setTimeout(() => document.addEventListener('click', closeOnClick), 10);
    });

    // Reset map view when popover is closed by the user (not programmatically)
    element.addEventListener('hidden.bs.popover', () => {
        if (!suppressReset) {
            resetMapView();
        }
    });
}

/**
 * Hide all visible popovers
 */
function hideAllPopovers() {
    suppressReset = true;
    document.querySelectorAll('.park-map__point, .park-map__point--expanded').forEach(el => {
        const popover = Popover.getInstance(el);
        if (popover) popover.hide();
    });
    suppressReset = false;
}

/**
 * Reset map to default view - collapse clusters, reset zoom, clear legend state
 */
function resetMapView() {
    if (svgOverlayRef) {
        collapseExpandedCluster(svgOverlayRef);
    }
    if (panzoomInstance) {
        panzoomInstance.reset({ animate: true });
    }
    if (legendItemsRef) {
        clearActivePoint(legendItemsRef);
    }
}

/**
 * Get BER icon URL for a given BER rating
 */
function getBerIconUrl(ber) {
    const themeUrl = (window.parkMapConfig || {}).themeUrl || '';
    if (!themeUrl || !ber) return null;
    // Normalize: remove spaces, e.g. "A 2" -> "A2"
    const normalized = ber.replace(/\s+/g, '');
    return `${themeUrl}/assets/images/ber/ber_${normalized}.svg`;
}

/**
 * Build HTML content for popover
 */
function buildPopoverContent(point) {
    let html = '<div class="park-map-popover__content">';

    if (point.image) {
        html += `<img src="${point.image}" alt="${point.label || ''}" class="park-map-popover__image">`;
    }

    // Meta fields
    const metaFields = [];
    // if (point.asset_number) metaFields.push({ label: 'Asset Number', value: point.asset_number });
    if (point.annual_rent) metaFields.push({ label: 'Passing Rent', value: '€' + Number(point.annual_rent).toLocaleString('en-IE') });
    if (point.floorspan_sqft) metaFields.push({ label: 'Floorspan (sq ft)', value: Number(point.floorspan_sqft).toLocaleString('en-IE') });
    // if (point.floorspan_sqm) metaFields.push({ label: 'Floorspan (sq m)', value: point.floorspan_sqm });
    if (point.park) metaFields.push({ label: 'Park', value: point.park });
    if (point.type) metaFields.push({ label: 'Type', value: point.type });

    if (metaFields.length) {
        html += '<div class="park-map-popover__meta">';
        metaFields.forEach(field => {
            html += `<div class="park-map-popover__meta-row"><strong>${field.label}:</strong> ${field.value}</div>`;
        });
        html += '</div>';
    }

    // BER icon (separate from meta, shown as image)
    if (point.ber) {
        const berIconUrl = getBerIconUrl(point.ber);
        if (berIconUrl) {
            html += `<div class="park-map-popover__ber"><img src="${berIconUrl}" alt="BER ${point.ber}"></div>`;
        }
    }

    if (point.tenants) {
        const tenantsList = point.tenants.split('\n').filter(t => t.trim());
        if (tenantsList.length) {
            html += '<div class="park-map-popover__tenants"><strong>Largest Tenant by Income</strong><ul>';
            tenantsList.forEach(tenant => {
                html += `<li>${tenant.trim()}</li>`;
            });
            html += '</ul></div>';
        }
    }

    html += '</div>';
    return html;
}

/**
 * Initialize cluster click behavior - expand to show individual points at their actual positions
 */
function initClusterBehavior(element, cluster, svg, settings, panzoom, mapContainer) {
    element.addEventListener('click', (e) => {
        // Hide any open popovers first
        hideAllPopovers();

        // Check if this cluster is already expanded
        if (expandedCluster && expandedCluster.cluster === cluster) {
            // Collapse - remove expanded points and reset zoom
            collapseExpandedCluster(svg);
            panzoom.reset({ animate: true });

            // Clear legend active state
            document.querySelectorAll('.park-map__legend-item--active').forEach(item => {
                item.classList.remove('park-map__legend-item--active');
            });
        } else {
            // Collapse any other expanded cluster first
            collapseExpandedCluster(svg);

            const scale = 3;
            const clusterCircle = element.querySelector('circle');
            const circleRect = clusterCircle.getBoundingClientRect();

            // Use zoomToPoint to zoom centered on the cluster
            panzoom.zoomToPoint(scale, {
                clientX: circleRect.left + circleRect.width / 2,
                clientY: circleRect.top + circleRect.height / 2
            }, { animate: true });

            // Hide cluster indicator and set as expanded
            element.style.display = 'none';
            expandedCluster = { element: element, cluster: cluster };

            // Show individual points at their actual positions after zoom starts
            setTimeout(() => {
                cluster.points.forEach((point) => {
                    const color = getPointColor(point, settings.pointColor || '#0d6efd');
                    const pointEl = createPointElement(
                        point,
                        color,
                        settings.pointRadius || 12
                    );
                    pointEl.classList.add('park-map__point--expanded');
                    svg.appendChild(pointEl);
                    expandedClusterPoints.push(pointEl);
                    initPopover(pointEl, point);

                    // Update reference to use the expanded point element
                    pointElements.set(point.id, { element: pointEl, point: point, isCluster: false });
                });

                // Update pin sizes for current zoom
                updateExpandedPinSizes(svg, settings.pointRadius || 12, scale);
            }, 100);
        }
    });
}
